// src/Studio/engine/followEngine.ts
// Follow Engine (Phase 5) - Element following and look-at behavior
// Computed at runtime; can optionally bake to keyframes

import type { Element, FollowTarget } from "../types";
import type { PropertyTrack } from "./keyframeEngine";

// ==================== Types ====================

export interface FollowResult {
  x?: number;           // Override x position
  y?: number;           // Override y position
  rotation?: number;    // Override rotation (for lookAt mode)
}

export interface FollowState {
  lastX: number;
  lastY: number;
  velocity: { x: number; y: number };
  lastRotation: number;
}

// ==================== Follow Computation ====================

/**
 * Compute follow position/rotation for an element at current time.
 * This is computed every frame during playback.
 */
export function computeFollowTransform(
  element: Element,
  followTarget: FollowTarget,
  targetElement: Element | undefined,
  playhead: number,
  previousState?: FollowState,
  opts?: { rotationOffsetDeg?: number }
): { result: FollowResult; state: FollowState } {
  const result: FollowResult = {};

  // Default state
  const state: FollowState = previousState ?? {
    lastX: element.x,
    lastY: element.y,
    velocity: { x: 0, y: 0 },
    lastRotation: element.rotation ?? 0,
  };

  if (!targetElement) {
    return { result, state };
  }

  const targetX = targetElement.x + (targetElement.width / 2);
  const targetY = targetElement.y + (targetElement.height / 2);

  if (followTarget.mode === "position") {
    // Position follow with optional offset and smoothing
    const offsetX = followTarget.offsetX ?? 0;
    const offsetY = followTarget.offsetY ?? 0;
    const smoothing = followTarget.smoothing ?? 0;

    // Target position (with offset)
    const goalX = targetX + offsetX - element.width / 2;
    const goalY = targetY + offsetY - element.height / 2;

    if (smoothing > 0 && previousState) {
      // Apply smoothing (exponential decay toward target)
      const factor = 1 - Math.min(1, smoothing);
      result.x = state.lastX + (goalX - state.lastX) * factor;
      result.y = state.lastY + (goalY - state.lastY) * factor;

      // Update state for next frame
      state.lastX = result.x;
      state.lastY = result.y;
    } else {
      // Direct follow
      result.x = goalX;
      result.y = goalY;
      state.lastX = goalX;
      state.lastY = goalY;
    }
  } else if (followTarget.mode === "lookAt") {
    // Look-at mode: rotate to face target
    const elementCenterX = element.x + element.width / 2;
    const elementCenterY = element.y + element.height / 2;

    const dx = targetX - elementCenterX;
    const dy = targetY - elementCenterY;

    const angleRad = Math.atan2(dy, dx);
    const angleDeg = (angleRad * 180) / Math.PI;
    const desiredAngleDeg = angleDeg + (opts?.rotationOffsetDeg ?? 0);

    const smoothing = followTarget.smoothing ?? 0;

    if (smoothing > 0 && previousState) {
      // Smooth rotation
      const currentRotation = Number.isFinite(previousState.lastRotation)
        ? previousState.lastRotation
        : (element.rotation ?? 0);
      const factor = 1 - Math.min(1, smoothing);

      // Handle angle wrapping
      let diff = desiredAngleDeg - currentRotation;
      while (diff > 180) diff -= 360;
      while (diff < -180) diff += 360;

      result.rotation = currentRotation + diff * factor;
      state.lastRotation = result.rotation;
    } else {
      result.rotation = desiredAngleDeg;
      state.lastRotation = desiredAngleDeg;
    }
  }

  return { result, state };
}

// ==================== Baking Follow to Keyframes ====================

/**
 * Bake follow behavior to keyframes for permanent animation.
 * This allows the animation to be exported without runtime computation.
 */
export function bakeFollowToKeyframes(
  element: Element,
  followTarget: FollowTarget,
  targetElement: Element,
  startTime: number,
  endTime: number,
  fps: number = 30
): PropertyTrack[] {
  const tracks: PropertyTrack[] = [];
  const numFrames = Math.ceil((endTime - startTime) * fps);

  if (numFrames < 2) return tracks;

  const xKeyframes: { time: number; value: number; easing: "linear" }[] = [];
  const yKeyframes: { time: number; value: number; easing: "linear" }[] = [];
  const rotationKeyframes: { time: number; value: number; easing: "linear" }[] = [];

  let state: FollowState | undefined;

  for (let i = 0; i <= numFrames; i++) {
    const t = startTime + (i / numFrames) * (endTime - startTime);

    const { result, state: newState } = computeFollowTransform(
      element,
      followTarget,
      targetElement,
      t,
      state
    );

    state = newState;

    if (followTarget.mode === "position") {
      if (result.x !== undefined) {
        xKeyframes.push({ time: t, value: result.x, easing: "linear" });
      }
      if (result.y !== undefined) {
        yKeyframes.push({ time: t, value: result.y, easing: "linear" });
      }
    } else if (followTarget.mode === "lookAt") {
      if (result.rotation !== undefined) {
        rotationKeyframes.push({ time: t, value: result.rotation, easing: "linear" });
      }
    }
  }

  // Reduce keyframes (remove redundant ones)
  if (xKeyframes.length > 0) {
    tracks.push({
      property: "x",
      keyframes: reduceKeyframes(xKeyframes, 0.5), // 0.5px tolerance
    });
  }

  if (yKeyframes.length > 0) {
    tracks.push({
      property: "y",
      keyframes: reduceKeyframes(yKeyframes, 0.5),
    });
  }

  if (rotationKeyframes.length > 0) {
    tracks.push({
      property: "rotation",
      keyframes: reduceKeyframes(rotationKeyframes, 0.1), // 0.1 degree tolerance
    });
  }

  return tracks;
}

/**
 * Reduce keyframes by removing ones where value doesn't change significantly.
 */
function reduceKeyframes<T extends { time: number; value: number; easing: "linear" }>(
  keyframes: T[],
  tolerance: number
): T[] {
  if (keyframes.length <= 2) return keyframes;

  const result: T[] = [keyframes[0]];
  let lastAddedIdx = 0;

  for (let i = 1; i < keyframes.length - 1; i++) {
    const prev = keyframes[lastAddedIdx];
    const curr = keyframes[i];
    const next = keyframes[i + 1];

    // Check if current point deviates significantly from linear interpolation
    const t = (curr.time - prev.time) / (next.time - prev.time);
    const interpolated = prev.value + (next.value - prev.value) * t;
    const deviation = Math.abs(curr.value - interpolated);

    if (deviation > tolerance) {
      result.push(curr);
      lastAddedIdx = i;
    }
  }

  result.push(keyframes[keyframes.length - 1]);
  return result;
}

// ==================== Follow Utilities ====================

/**
 * Get center point of an element.
 */
export function getElementCenter(element: Element): { x: number; y: number } {
  return {
    x: element.x + element.width / 2,
    y: element.y + element.height / 2,
  };
}

/**
 * Calculate angle from one element to another.
 */
export function getAngleBetweenElements(
  fromElement: Element,
  toElement: Element
): number {
  const from = getElementCenter(fromElement);
  const to = getElementCenter(toElement);

  const dx = to.x - from.x;
  const dy = to.y - from.y;

  return (Math.atan2(dy, dx) * 180) / Math.PI;
}

/**
 * Calculate distance between two elements (center to center).
 */
export function getDistanceBetweenElements(
  element1: Element,
  element2: Element
): number {
  const c1 = getElementCenter(element1);
  const c2 = getElementCenter(element2);

  return Math.sqrt((c2.x - c1.x) ** 2 + (c2.y - c1.y) ** 2);
}
